import styled, { css } from "styled-components";
import { theme } from "../../../styles/theme";
import type { InputSize } from "./types";

/* =========================================================================
 * Legacy styles (default export <Input />) — preservados intactos
 * ========================================================================= */

interface InputWrapperProps {
  $theme: string;
  $variant: string;
  $message?: string | undefined;
}

interface StyledInputProps {
  $hasIcon?: boolean;
}

export const InputWrapper = styled.div<InputWrapperProps>`
  position: relative;

  ${({ $theme }) =>
    $theme === "modern" &&
    css`
      input {
        appearance: active;
        display: block;
        background: #ffffff;
        border: 1px solid #d2d3e5;
        box-sizing: border-box;
        border-radius: 8px;
        width: 100%;
        padding: 0.75rem;
        font-size: 1rem;
        border-radius: 8px;
        padding-right: 2.5rem;
        padding-left: 2rem;
        color: #1f2937;
        transition: all 0.3s ease;

        &:focus {
          border-color: ${theme.colors.primary};
          outline: none;
          background-color: #fffbf7;
          box-shadow: 0 0 0 3px rgba(236, 103, 38, 0.1);
        }

        &:focus-visible {
          outline: none;
        }

        &:hover {
          border-color: ${theme.colors.primary};
        }

        &:disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }
      }
    `}

  ${({ $theme }) =>
    $theme === "legacy" &&
    css`
        input {
          appearance: active;
          display: block;
          background: #ffffff;
          border: 1px solid #d2d3e5;
          box-sizing: border-box;
          border-radius: 8px;
          margin-top: 5px;
          padding: 1rem 1.75rem;
          width: 100%;
          transition: all 0.3s ease;

          &:focus {
            border-color: ${theme.colors.primary};
            outline: none;
            background-color: #fffbf7;
            box-shadow: 0 0 0 3px rgba(236, 103, 38, 0.1);
          }

          &:focus-visible {
            outline: none;
          }

          &:hover {
            border-color: ${theme.colors.primary};
          }

          &:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
          }
    `}
`;

export const StyledLabel = styled.label`
  display: block;
  min-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

export const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border-radius: 8px;
  padding-right: 2.5rem;
  color: #1f2937;
  outline: none;
  padding-left: ${({ $hasIcon }) => ($hasIcon ? "2rem" : ".5rem")};

  &[type="date"] {
    cursor: pointer;
  }
`;

export const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
`;

export const ErrorMessage = styled.span`
  font-size: 12px;
  position: absolute;
  left: 0;
  top: 100%;
  color: red;
  margin-top: 0.25rem;
`;

/* =========================================================================
 * Novo Input primitivo (named export { Input })
 * ========================================================================= */

const sizes: Record<InputSize, ReturnType<typeof css>> = {
  sm: css`
    padding: 10px 14px;
    font-size: 13px;
  `,
  md: css`
    height: ${({ theme }) => theme.sizes.controlHeight};
    padding: 0 16px;
    font-size: 14px;
  `,
  lg: css`
    padding: 16px 18px;
    font-size: 16px;
  `,
};

export const Wrap = styled.div`
  position: relative;
  display: inline-flex;
  width: 100%;
`;

export const PrefixSlot = styled.span<{ $error?: boolean }>`
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme, $error }) =>
    $error ? theme.colors.danger : theme.colors.fg4};
  font-size: 18px;
  pointer-events: none;
  transition: color ${({ theme }) => theme.motion.durationBase};
  z-index: 1;
`;

export const SuffixSlot = styled.span<{ $error?: boolean }>`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme, $error }) =>
    $error ? theme.colors.danger : theme.colors.fg4};
  font-size: 18px;
  pointer-events: none;
  z-index: 1;
`;

export const Field = styled.input<{
  $size: InputSize;
  $hasPrefix: boolean;
  $hasSuffix: boolean;
  $error: boolean;
}>`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.senneCream};
  border: 2px solid transparent;
  border-radius: ${({ theme }) => theme.radii.md};
  font-family: ${({ theme }) => theme.typography.fontBody};
  color: ${({ theme }) => theme.colors.fg1};
  outline: none;
  transition: all ${({ theme }) => theme.motion.durationBase};
  ${({ $size }) => sizes[$size]}
  ${({ $hasPrefix }) =>
    $hasPrefix &&
    css`
      padding-left: 48px;
    `}
  ${({ $hasSuffix }) =>
    $hasSuffix &&
    css`
      padding-right: 48px;
    `}

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.senneWhite};
    box-shadow: ${({ theme }) => theme.shadows.focusRing};
  }

  ${Wrap}:focus-within ${PrefixSlot} {
    color: ${({ theme }) => theme.colors.primary};
  }

  ${({ $error, theme }) =>
    $error &&
    css`
      background: ${theme.colors.dangerSoft};
      border-color: #fecaca;
      color: #7f1d1d;
    `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
